import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Brain, Mail, Lock, User, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import DoneForYouCallout from '../components/DoneForYouCallout';

const Auth = () => {
  const [signUpData, setSignUpData] = useState({ email: '', password: '', fullName: '' });
  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();

  const handleForgotPassword = async () => {
    const email = signInData.email.trim();
    if (!email) { setResetMsg('Enter your email above first.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset`
    });
    setLoading(false);
    setResetMsg(error ? `Error: ${error.message}` : `Reset link sent to ${email}`);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Google sign-in error:', error);
      alert('Google sign-in failed. Please make sure it is configured in Supabase.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { data, error } = await signUp(signUpData.email, signUpData.password, signUpData.fullName);
    setLoading(false);
    
    if (!error && data) {
      // Navigate to home after successful signup
      setTimeout(() => navigate('/'), 2000);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { data, error } = await signIn(signInData.email, signInData.password);
    setLoading(false);
    
    if (!error && data) {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6">
      <div className="max-w-md w-full">
        <DoneForYouCallout />
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-blue-500/10 rounded-full mb-4">
            <Brain className="w-12 h-12 text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">globalnow.biz</h1>
          <p className="text-gray-400">Self-service tools account</p>
        </div>

        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            {/* Sign In Tab */}
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={signInData.email}
                      onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={signInData.password}
                      onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-blue-400 hover:text-blue-300 underline"
                  >
                    Forgot password?
                  </button>
                  {resetMsg && <p className="text-xs mt-2 text-gray-400">{resetMsg}</p>}
                </div>

                {/* Temporarily disabled - configure Google OAuth in Supabase to enable */}
                {/* <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-800 text-gray-400">Or continue with</span>
                  </div>
                </div>
                <Button type="button" onClick={handleGoogleSignIn} disabled={loading} variant="outline" className="w-full border-gray-700 bg-white hover:bg-gray-100 text-gray-900" size="lg">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">...</svg>
                  Sign in with Google
                </Button> */}
              </form>
            </TabsContent>

            {/* Sign Up Tab */}
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={signUpData.fullName}
                      onChange={(e) => setSignUpData({ ...signUpData, fullName: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={signUpData.email}
                      onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={signUpData.password}
                      onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-sm text-gray-300 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 mt-0.5 text-blue-400" />
                    This free account is for the self-service tools. To have us build your website, use the order link above; no account is required.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700"
                  size="lg"
                >
                  {loading ? 'Creating Account...' : 'Create Free Account'}
                </Button>

                {/* Temporarily disabled - configure Google OAuth in Supabase to enable */}
                {/* <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-800 text-gray-400">Or continue with</span>
                  </div>
                </div>
                <Button type="button" onClick={handleGoogleSignIn} disabled={loading} variant="outline" className="w-full border-gray-700 bg-white hover:bg-gray-100 text-gray-900" size="lg">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">...</svg>
                  Sign up with Google
                </Button> */}
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        <p className="text-center text-gray-500 text-sm mt-6">
          By signing up, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Auth;
